import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 * 
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { useOnMount } from "abreact/utils/AbHooks";
import { ModuleLoaderUtils } from "abweb/utils/ModuleLoaderUtils";
import { ExtensionPoints } from "abweb/utils/plugins/ExtensionPoints";
/**
 * An extension point of all registered Easter eggs.
 */
export const EasterEggs = ExtensionPoints.syncMultiValue();

/**
 * A custom hook to add the application Easter Eggs onto the page.
 * 
 * To invoke the Easter egg, use the following keyboard shortcuts:
 * 
 *   Windows: control-alt-shift + key
 *   Mac: command-shift + function + key
 * 
 * The installed Easter eggs are as follows:
 * 
 *   F10 - theming tweaks
 *   F12 - translator tools
 */
export function useAppEasterEggs(forceAppRedraw) {
  const appShortcutHandlers = event => {
    if (event.shiftKey && (event.altKey && event.ctrlKey || event.metaKey)) {
      if (event.key === "F12") {
        showTranslatorToolsDialog(forceAppRedraw);
      }
      if (event.key === "F10") {
        showEasterEggs();
      }
    }
  };
  useOnMount(() => {
    window.addEventListener('keydown', appShortcutHandlers);
    return () => {
      window.removeEventListener('keydown', appShortcutHandlers);
    };
  });
}
function showTranslatorToolsDialog(forceAppRedraw) {
  // Do NOT incur cost of transitively loading all debug utilities until the user shows
  // the dialog for the first time (and definitely not before first app render!)
  ModuleLoaderUtils.loadExport(() => import("abreact/incubator/ui/translatorTools/TranslatorToolsDialog"), "showTranslatorToolsDialog").then(showTranslatorToolsDialog => showTranslatorToolsDialog({
    forceAppRedraw: forceAppRedraw
  }));
}
function showEasterEggs() {
  // Do NOT incur cost of transitively loading all easter eggs until the user shows
  // the dialog for the first time (and definitely not before first app render!)
  ModuleLoaderUtils.loadExport(() => import("abreact/app/easterEggs/AbEasterEggDialog"), "showEasterEggDialog").then(showEasterEggDialog => showEasterEggDialog());
}

/**
 * An abstract implementation of an Easter egg that is persisted using browser local storage.
 */
export class LocalStorageEasterEgg {
  constructor(easterEggID, displayName, description) {
    _defineProperty(this, "storageID", void 0);
    _defineProperty(this, "defaultValueOverride", undefined);
    this.displayName = displayName;
    this.description = description;
    this.storageID = `${LocalStorageEasterEgg.EASTER_EGGS_NAMESPACE}.${easterEggID}`;
  }
  get defaultValue() {
    return this.defaultValueOverride ?? false;
  }
  setDefaultValueOverride(defaultValueOverride) {
    this.defaultValueOverride = defaultValueOverride;
  }
  isAvailable() {
    return true;
  }
  isEnabled() {
    const currentValue = window.localStorage.getItem(this.storageID);
    if (currentValue === null) {
      return this.defaultValue;
    }
    return currentValue === "true";
  }
  setEnabled(enabled) {
    if (enabled !== this.defaultValue) window.localStorage.setItem(this.storageID, enabled ? "true" : "false");else window.localStorage.removeItem(this.storageID);
  }
}
_defineProperty(LocalStorageEasterEgg, "EASTER_EGGS_NAMESPACE", "AbInitio.EasterEgg");
//# sourceMappingURL=EasterEggs.js.map
